import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { Currency } from "dinero.js";
import React, { useContext, useEffect, useState } from "react";
import Loader from "src/Components/Common/Loader";
import useFetch from "src/Components/Common/useFetch";
import { NdButton } from "src/Components/Recon360/PartnerCommunication/MsmePartnerPortal/CommonComponents";
import { UseCaseType } from "src/entity/recon-entity/ReconInterfaces";
import FormatDate from "src/Utils/DateUtils";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";
import { BCPortalCBDetails } from "../../../BalanceConfirmationPortal/BalanceConfirmationPortalCommon";
import BcNewContext from "../BcNewContext";

interface UpdateResponseDialogProps {
  open: boolean;
  setOpen: (_val: boolean) => void;
  onConfirm: () => void;
  onNotConfirm: () => void;
}

const UpdateResponseStep1: React.FC<UpdateResponseDialogProps> = ({ open, setOpen, onConfirm, onNotConfirm }) => {
  const { currentRow } = useContext(BcNewContext);
  const [loading, setLoading] = useState(false);
  const [bcPortalCbDetails, setBcPortalCbDetails] = useState<BCPortalCBDetails>({} as any);
  const onClose = () => {
    setOpen(false);
  };

  const setBalanceConfirmationStatus = async (isBalanceConfirmed: boolean) => {
    await useFetch("/api/PartnerCommunicationPortal/SetConfirmationStatusFromTool", "POST", {
      failureMessage: "Failed to set confirmation status",
      data: {
        mappingId: currentRow.mappingId,
        communicationId: currentRow.newMailInitiationCount,
        companyId: currentRow.companyId,
        branchCode: currentRow.branchCode,
        businessPartnerId: currentRow.businessPartnerId,
        isBalanceConfirmed,
        useCaseType: UseCaseType.balanceConfirmationBeta,
      },
    });
  };

  const GetClosingBalanceDetailsForTool = () => {
    setLoading(true);
    useFetch<{ closingBalanceDetails: BCPortalCBDetails }>(
      "/api/PartnerCommunicationPortal/GetClosingBalanceDetailsForTool",
      "GET",
      {
        failureMessage: "Unable to fetch Closing Balance Details",
        config: {
          params: {
            mappingId: currentRow.mappingId,
            communicationId: currentRow.newMailInitiationCount,
            companyId: currentRow.companyId,
            branchCode: currentRow.branchCode,
          },
        },
        thenCallBack: (res) => {
          setBcPortalCbDetails(res.data.closingBalanceDetails || ({} as any));
          setLoading(false);
        },
      }
    );
  };

  useEffect(() => {
    if (open) GetClosingBalanceDetailsForTool();
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Update Response (Step: 1/3)</Typography>
      </DialogTitle>

      {loading ? (
        <Loader />
      ) : (
        <DialogContent sx={{ borderTop: 1, borderBottom: 1, borderColor: "divider", py: "16px !important" }}>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            Confirm / Not Confirm the closing balance basis on partner's response
          </Typography>

          <Box className="d_flex" gap={2}>
            <Box
              className={"vertical_center_align cb_date_card"}
              bgcolor="#eee8ed"
              px={"12px"}
              gap={3}
              py={1}
              borderRadius={1}
            >
              <Box>
                <Typography variant="overline">Closing Balance</Typography>
                <Box className="vertical_center_align" gap={1}>
                  <Chip
                    label={Number(bcPortalCbDetails.ownClosingBalance) > 0 ? "Receivable" : "Payable"}
                    color="default"
                    size="small"
                    variant="filled"
                    sx={{ "& > *": { fontFamily: "inherit" } }}
                  />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {bcPortalCbDetails.ownClosingBalance === null
                      ? null
                      : formatMoney(
                          ToDineroObj(
                            Number(bcPortalCbDetails.ownClosingBalance),
                            bcPortalCbDetails.currency as Currency
                          )
                        )}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="overline">as on</Typography>
              <Box>
                <Typography variant="overline">Closing Balance Date</Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {FormatDate(bcPortalCbDetails.closingBalanceDate).toString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      )}

      <DialogActions sx={{ p: 1 }}>
        <Button variant="text" onClick={onClose}>
          CANCEL
        </Button>
        <Box className="d_flex">
          <NdButton
            variant="contained"
            color="secondary"
            onClick={async () => {
              onNotConfirm();
              await setBalanceConfirmationStatus(false);
            }}
            sx={{ mr: 1 }}
          >
            NOT CONFIRM BALANCE
          </NdButton>
          <NdButton
            variant="contained"
            color="primary"
            onClick={async () => {
              onConfirm();
              await setBalanceConfirmationStatus(true);
            }}
          >
            CONFIRM BALANCE
          </NdButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateResponseStep1;
